import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import DOMPurify from 'dompurify';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from '@/components/ui/toast';
import {
  ArrowUpDown,
  ChevronDown,
  Clock3,
  Copy,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  KeyRound,
  Link2,
  LockOpen,
  MessageCircleReply,
  Paperclip,
  RefreshCw,
  Search,
  Send,
  Shield,
  Smartphone,
  UserPlus,
  Users,
  Wrench,
  X
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useUser } from '@/store/useUser';
import { policyMatches } from '@/lib/policies';
import { UserSecurityPermissionsManager } from '../userAccounts/components/UserSecurityPermissionsManager';
import { PermissionManager, PermissionPresetSelect } from '../userAccounts/components/permissionManager';
import {
  LIST_USER_SECURITY_POLICIES,
  UPDATE_USER_SECURITY_POLICIES
} from '../userAccounts/queries';
import { USER_MANAGER_PERMISSION_TEMPLATE_MANAGED_VALUES } from '../userAccounts/userManagerPermissionTemplates';
import {
  applyPolicyBackedPermissionAccessPolicies,
  getPoliciesForPolicyBackedPermissionAccess,
  isPolicyBackedPermissionAccess
} from '@/utils/userPermissionAccessPolicies';
import { validatePasswordSimple } from '@/utils/passwordValidation';
import {
  ProvisionUserAccountRequestDialog,
  ProvisionUserAccountsPage
} from '../userAccountRequests';
import type {
  UserAccountProvisionResult,
  UserAccountRequestRow
} from '../userAccountRequests';

const LIST_SUPPORT_TICKETS = gql`
  query ListSupportTickets($filters: SupportTicketListFiltersInput) {
    listSupportTickets(filters: $filters) {
      id
      userid
      username
      email
      message
      messageFormat
      status
      requestType
      createdAt
      updatedAt
      lastResponseAt
      closedAt
      ageHours
      isOverdue
      responseCount
      noteCount
      lastResponderName
      requesterName
      requesterEmail
      assignedUsers {
        userId
        username
        name
        email
      }
    }
  }
`;

const GET_SUPPORT_TICKET = gql`
  query GetSupportTicket($ticketId: Int!) {
    getSupportTicket(ticketId: $ticketId) {
      ticket {
        id
        userid
        username
        email
        message
        messageFormat
        status
        requestType
        createdAt
        updatedAt
        lastResponseAt
        closedAt
        ageHours
        isOverdue
        responseCount
        noteCount
        lastResponderName
        requesterUsername
        requesterName
        requesterEmail
        requesterDisabled
        requesterFirstLogin
        requesterTwoFactorEnabled
        requesterPasswordFailures
        requesterCreatedDate
        requesterEntryDate
        requesterHandlerUsername
        requesterHandlerName
        requesterHandlerEmail
        requesterNote
        assignedUsers {
          assignmentId
          userId
          username
          name
          email
          createdAt
          assignedByName
        }
        attachments {
          id
          filename
          mimeType
          data
          sizeBytes
          createdAt
        }
      }
      replies {
        id
        ticketId
        message
        messageFormat
        createdByUserId
        username
        email
        createdAt
        emailSentAt
        attachments {
          id
          filename
          mimeType
          data
          sizeBytes
          createdAt
        }
      }
      notes {
        id
        ticketId
        note
        createdByUserId
        username
        createdAt
      }
    }
  }
`;

const GET_ACCOUNT_REQUEST_BY_SUPPORT_TICKET = gql`
  query UserAccountRequestBySupportTicket($ticketId: Int!) {
    userAccountRequestBySupportTicket(ticketId: $ticketId) {
      id
      supportTicketId
      requestType
      requesterName
      requestedName
      emailAddress
      organizationName
      siteName
      programName
      roleName
      accessReason
      status
      createdAt
    }
  }
`;

const RESPOND_SUPPORT_TICKET = gql`
  mutation RespondSupportTicket($ticketId: Int!, $message: String!, $status: String) {
    respondSupportTicket(ticketId: $ticketId, message: $message, status: $status) {
      id
      ticketId
      message
      createdByUserId
      username
      createdAt
      emailSentAt
    }
  }
`;

const ADD_SUPPORT_TICKET_PRIVATE_NOTE = gql`
  mutation AddSupportTicketPrivateNote($ticketId: Int!, $note: String!) {
    addSupportTicketPrivateNote(ticketId: $ticketId, note: $note) {
      id
      ticketId
      note
      createdByUserId
      username
      createdAt
    }
  }
`;

const UPDATE_SUPPORT_TICKET_STATUS = gql`
  mutation UpdateSupportTicketStatus($ticketId: Int!, $status: String!) {
    updateSupportTicketStatus(ticketId: $ticketId, status: $status) {
      id
      status
      updatedAt
      closedAt
      ageHours
      isOverdue
    }
  }
`;

const LIST_SUPPORT_ASSIGNABLE_USERS = gql`
  query ListSupportAssignableUsers($limit: Int) {
    listSupportAssignableUsers(limit: $limit) {
      userId
      username
      name
      email
    }
  }
`;

const ASSIGN_SUPPORT_TICKET_USER = gql`
  mutation AssignSupportTicketUser($ticketId: Int!, $userId: Int!) {
    assignSupportTicketUser(ticketId: $ticketId, userId: $userId) {
      assignmentId
      ticketId
      userId
      username
      name
      email
      createdAt
      assignedByName
    }
  }
`;

const UNASSIGN_SUPPORT_TICKET_USER = gql`
  mutation UnassignSupportTicketUser($ticketId: Int!, $userId: Int!) {
    unassignSupportTicketUser(ticketId: $ticketId, userId: $userId) {
      assignmentId
      ticketId
      userId
      username
      name
      email
      createdAt
      assignedByName
    }
  }
`;

const SEND_TOTP_ENROLLMENT_EMAIL = gql`
  mutation SendTwoFactorEnrollmentNotification($input: TwoFactorEnrollmentNotificationInput!) {
    sendTwoFactorEnrollmentNotification(input: $input) {
      success
      enrollmentUrl
      status
    }
  }
`;

const SEND_TEMP_COMPLIANCE_LOGIN = gql`
  mutation SendTempComplianceLogin($username: String!, $resetPassword: Boolean!, $resetTotp: Boolean!) {
    sendTempComplianceLogin(username: $username, resetPassword: $resetPassword, resetTotp: $resetTotp) {
      success
      message
      loginUrl
      expiresAt
    }
  }
`;

const MODIFY_USER = gql`
  mutation ModifyUser($username: String!, $password: String, $passwordfailures: Int) {
    modifyUser(username: $username, password: $password, passwordfailures: $passwordfailures) {
      message
    }
  }
`;

type SortDirection = 'ASC' | 'DESC';
type SupportToolDialog =
  | 'reset-password'
  | 'set-password'
  | 'permissions'
  | 'two-factor'
  | 'one-time-login'
  | 'unlock'
  | 'ticket-password-reset'
  | 'ticket-authenticator-reset';
type SupportToolRequirement = 'username' | 'userId';
type SupportToolMenuItem = {
  tool: SupportToolDialog;
  label: string;
  requirement: SupportToolRequirement;
  Icon: React.ComponentType<{ className?: string }>;
};
const ACTIVE_STATUS_FILTER = 'active';
const STALE_PENDING_USER_HOURS = 72;

const SUPPORT_TOOL_MENU_ITEMS = ([
  { tool: 'one-time-login', label: 'Send One-Time Login', requirement: 'username', Icon: Link2 },
  { tool: 'reset-password', label: 'Send Password Reset', requirement: 'username', Icon: Send },
  { tool: 'two-factor', label: 'Send Two-Factor Auth', requirement: 'userId', Icon: Smartphone },
  { tool: 'set-password', label: 'Set Password', requirement: 'username', Icon: KeyRound },
  { tool: 'unlock', label: 'Unlock Account', requirement: 'username', Icon: LockOpen },
  { tool: 'permissions', label: 'Update Permissions', requirement: 'username', Icon: Shield },
] as SupportToolMenuItem[]).sort((left, right) => left.label.localeCompare(right.label));

const statusOptions = [
  { value: ACTIVE_STATUS_FILTER, label: 'Open + Pending User' },
  { value: 'all', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'pending_user', label: 'Pending User' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];
const writableStatusOptions = statusOptions.filter(
  (item) => item.value !== 'all' && item.value !== ACTIVE_STATUS_FILTER
);

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const formatDateOnly = (value: string | null | undefined) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const truncate = (value: string | null | undefined, max = 80) => {
  const text = `${value || ''}`;
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
};

const getStatusBadgeClasses = (status: string, isOverdue: boolean, ageHours: number) => {
  if (status === 'open' && isOverdue) return 'bg-red-100 text-red-800 border-red-200';
  if (status === 'open') return 'bg-amber-100 text-amber-800 border-amber-200';
  if (status === 'pending_user' && ageHours >= STALE_PENDING_USER_HOURS) {
    return 'bg-slate-100 text-slate-500 border-slate-200';
  }
  if (status === 'pending_user') return 'bg-blue-100 text-blue-800 border-blue-200';
  if (status === 'resolved') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (status === 'closed') return 'bg-slate-100 text-slate-800 border-slate-200';
  return 'bg-slate-100 text-slate-800 border-slate-200';
};

const getAgeBadgeClasses = (status: string, ageHours: number) => {
  if (status === 'open' && ageHours >= 48) return 'bg-red-100 text-red-800 border-red-200';
  if (status === 'open' && ageHours >= 24) return 'bg-amber-100 text-amber-800 border-amber-200';
  if (status === 'open') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  return 'bg-slate-100 text-slate-800 border-slate-200';
};

const ageLabel = (ageHours: number) => {
  if (!Number.isFinite(ageHours) || ageHours < 0) return '—';
  if (ageHours < 1) return '<1h';
  if (ageHours < 24) return `${ageHours}h`;
  const days = Math.floor(ageHours / 24);
  const hours = ageHours % 24;
  if (hours === 0) return `${days}d`;
  return `${days}d ${hours}h`;
};

const getPolicyList = (user: any) => {
  const policies = Array.isArray(user?.policies) ? user.policies : [];
  const permissions = Array.isArray(user?.permissions) ? user.permissions : [];
  return [...policies, ...permissions].map((item) => `${item || ''}`.trim()).filter(Boolean);
};

const hasAnyPolicy = (user: any, requiredPolicies: string[]) => {
  const policyList = getPolicyList(user);
  if (policyList.includes('*:*~*') || policyList.includes('system:fullaccess')) return true;
  return requiredPolicies.some((required) => policyList.some((policy) => policyMatches(policy, required)));
};

const looksLikeHtml = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value || '');

const sanitizeMessageHtml = (value: string) => DOMPurify.sanitize(value || '', {
  ADD_ATTR: ['target', 'rel'],
  FORBID_ATTR: ['style'],
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
});

const copyText = async (value: string) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const element = document.createElement('textarea');
  element.value = value;
  element.style.position = 'fixed';
  element.style.left = '-9999px';
  document.body.appendChild(element);
  element.focus();
  element.select();
  document.execCommand('copy');
  document.body.removeChild(element);
};

const formatBytes = (value: number | null | undefined) => {
  const size = Number(value || 0);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getRandomIndex = (max: number) => {
  if (max <= 0) return 0;
  const cryptoObject = typeof window !== 'undefined' ? window.crypto : undefined;
  if (cryptoObject?.getRandomValues) {
    const values = new Uint32Array(1);
    cryptoObject.getRandomValues(values);
    return values[0] % max;
  }
  return Math.floor(Math.random() * max);
};

const generatePassword = () => {
  const groups = [
    'ABCDEFGHJKLMNPQRSTUVWXYZ',
    'abcdefghijkmnopqrstuvwxyz',
    '23456789',
    '!@#$%^&*?'
  ];
  const allCharacters = groups.join('');
  const password = groups.map((group) => group[getRandomIndex(group.length)]);

  while (password.length < 16) {
    password.push(allCharacters[getRandomIndex(allCharacters.length)]);
  }

  for (let index = password.length - 1; index > 0; index -= 1) {
    const swapIndex = getRandomIndex(index + 1);
    [password[index], password[swapIndex]] = [password[swapIndex], password[index]];
  }

  return password.join('');
};

const normalizePolicyKey = (value: unknown) => `${value || ''}`.trim().toLowerCase();

const normalizePolicyList = (values: string[]) => {
  const normalizedValues: string[] = [];
  const seen = new Set<string>();

  (Array.isArray(values) ? values : []).forEach((value) => {
    const trimmed = `${value || ''}`.trim();
    const key = normalizePolicyKey(trimmed);
    if (!key || seen.has(key)) return;
    seen.add(key);
    normalizedValues.push(trimmed);
  });

  return normalizedValues;
};

const policyListFingerprint = (values: string[]) =>
  normalizePolicyList(values)
    .map((value) => normalizePolicyKey(value))
    .sort()
    .join('|');

const simpleManagedValues = USER_MANAGER_PERMISSION_TEMPLATE_MANAGED_VALUES;
const simpleManagedPolicyValues = Array.from(new Set(
  simpleManagedValues.flatMap((value) => {
    if (isPolicyBackedPermissionAccess(value)) {
      return getPoliciesForPolicyBackedPermissionAccess(value);
    }
    return [value];
  })
)).map((value) => normalizePolicyKey(value));

type SupportPermissionState = {
  permission: string;
  enabled: boolean;
};

type SupportToolActionDialogProps = {
  open: boolean;
  title: string;
  description: string;
  actionLabel: string;
  loading?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  requesterName: string;
  username: string;
  email: string;
  userId?: number | null;
  failedAttempts?: number | null;
  resultUrl?: string;
  resultUrlLabel?: string;
  resultExpiresAt?: string;
  onConfirm: () => Promise<void>;
  onOpenChange: (open: boolean) => void;
};

const SupportToolActionDialog: React.FC<SupportToolActionDialogProps> = ({
  open,
  title,
  description,
  actionLabel,
  loading = false,
  disabled = false,
  icon,
  requesterName,
  username,
  email,
  userId,
  failedAttempts,
  resultUrl,
  resultUrlLabel = 'Generated Link',
  resultExpiresAt,
  onConfirm,
  onOpenChange,
}) => {
  const displayedResultUrl = `${resultUrl || ''}`.trim();

  const handleCopyResultUrl = async () => {
    if (!displayedResultUrl) return;
    try {
      await copyText(displayedResultUrl);
      toast.success('Link copied.');
    } catch (copyError: any) {
      toast.error(copyError?.message || 'Failed to copy link.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        contentWidth="540px"
        contentHeight="auto"
        contentMaxWidth="94vw"
        contentMaxHeight="80vh"
        contentTop="10vh"
        className="z-[70]"
        overlayClassName="bg-black/30"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            {icon}
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border bg-gray-50 p-3 text-sm">
            <div><span className="font-medium">Name:</span> {requesterName || 'Unknown User'}</div>
            <div><span className="font-medium">Username:</span> {username || 'N/A'}</div>
            <div><span className="font-medium">Email:</span> {email || 'N/A'}</div>
            {userId ? <div><span className="font-medium">User ID:</span> {userId}</div> : null}
            {typeof failedAttempts === 'number' ? (
              <div><span className="font-medium">Failed attempts:</span> {failedAttempts}</div>
            ) : null}
          </div>

          {displayedResultUrl && (
            <div className="rounded-md border bg-white p-3">
              <div className="mb-2 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase text-gray-600">{resultUrlLabel}</p>
                  {resultExpiresAt && (
                    <p className="text-xs text-gray-500">Expires: {formatDateTime(resultExpiresAt)}</p>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCopyResultUrl}
                    aria-label="Copy generated link"
                    title="Copy generated link"
                    className="h-8 w-8 p-0"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    asChild
                    aria-label="Open generated link"
                    title="Open generated link"
                    className="h-8 w-8 p-0"
                  >
                    <a href={displayedResultUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>
              <input
                readOnly
                value={displayedResultUrl}
                className="h-9 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-xs text-gray-800"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" onClick={onConfirm} disabled={disabled || loading}>
            {loading ? 'Working...' : actionLabel}
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

type SupportSetPasswordDialogProps = {
  open: boolean;
  loading?: boolean;
  requesterName: string;
  username: string;
  email: string;
  userId?: number | null;
  onSetPassword: (password: string) => Promise<void>;
  onOpenChange: (open: boolean) => void;
};

const SupportSetPasswordDialog: React.FC<SupportSetPasswordDialogProps> = ({
  open,
  loading = false,
  requesterName,
  username,
  email,
  userId,
  onSetPassword,
  onOpenChange,
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPassword('');
    setShowPassword(false);
  }, [open]);

  const handleGeneratePassword = () => {
    setPassword(generatePassword());
    setShowPassword(true);
  };

  const handleCopyPassword = async () => {
    const nextPassword = password.trim();
    if (!nextPassword) return;
    try {
      await copyText(nextPassword);
      toast.success('Password copied.');
    } catch (copyError: any) {
      toast.error(copyError?.message || 'Failed to copy password.');
    }
  };

  const handleSetPassword = async () => {
    const nextPassword = password.trim();
    const passwordError = validatePasswordSimple(nextPassword);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }
    await onSetPassword(nextPassword);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        contentWidth="560px"
        contentHeight="auto"
        contentMaxWidth="94vw"
        contentMaxHeight="80vh"
        contentTop="10vh"
        className="z-[70]"
        overlayClassName="bg-black/30"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <KeyRound className="h-4 w-4 text-blue-600" />
            Set Password
          </DialogTitle>
          <DialogDescription>
            Set a new password directly for this user and clear failed password attempts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border bg-gray-50 p-3 text-sm">
            <div><span className="font-medium">Name:</span> {requesterName || 'Unknown User'}</div>
            <div><span className="font-medium">Username:</span> {username || 'N/A'}</div>
            <div><span className="font-medium">Email:</span> {email || 'N/A'}</div>
            {userId ? <div><span className="font-medium">User ID:</span> {userId}</div> : null}
          </div>

          <div>
            <Label htmlFor="support-tool-set-password" className="mb-1 block text-xs font-semibold uppercase text-gray-600">
              New Password
            </Label>
            <div className="flex gap-2">
              <Input
                id="support-tool-set-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                disabled={loading}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowPassword((current) => !current)}
                disabled={!password.trim() || loading}
                title={showPassword ? 'Hide password' : 'Show password'}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="h-9 w-9 p-0"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGeneratePassword}
                disabled={loading}
                title="Generate password"
                aria-label="Generate password"
                className="h-9 w-9 p-0"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyPassword}
                disabled={!password.trim() || loading}
                title="Copy password"
                aria-label="Copy password"
                className="h-9 w-9 p-0"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" onClick={handleSetPassword} disabled={!username || !password.trim() || loading}>
            {loading ? 'Setting...' : 'Set Password'}
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

type SupportPermissionsDialogProps = {
  open: boolean;
  username: string;
  requesterName: string;
  onToolRun: () => Promise<void>;
  onOpenChange: (open: boolean) => void;
};

const SupportPermissionsDialog: React.FC<SupportPermissionsDialogProps> = ({
  open,
  username,
  requesterName,
  onToolRun,
  onOpenChange,
}) => {
  const [directPolicies, setDirectPolicies] = useState<string[]>([]);
  const [draftDirectPolicies, setDraftDirectPolicies] = useState<string[]>([]);
  const [directPoliciesLoaded, setDirectPoliciesLoaded] = useState(false);
  const [isSavingPermissions, setIsSavingPermissions] = useState(false);

  const {
    data: userSecurityPoliciesData,
    loading: policiesLoading,
    error: policiesError,
    refetch: refetchUserSecurityPolicies,
  } = useQuery(LIST_USER_SECURITY_POLICIES, {
    variables: { username },
    skip: !open || !username,
    fetchPolicy: 'cache-and-network',
  });
  const [updateUserSecurityPolicies] = useMutation(UPDATE_USER_SECURITY_POLICIES);

  useEffect(() => {
    if (!open) return;
    setDirectPolicies([]);
    setDraftDirectPolicies([]);
    setDirectPoliciesLoaded(false);
  }, [open, username]);

  useEffect(() => {
    if (!open) return;
    const rows = userSecurityPoliciesData?.listUserSecurityPolicies;
    if (!Array.isArray(rows)) return;

    const nextPolicies = normalizePolicyList(
      rows
        .filter((row: any) => row?.allow !== false)
        .map((row: any) => `${row?.policy || ''}`.trim())
        .filter((policy: string) => policy.length > 0)
    );
    setDirectPolicies(nextPolicies);
    setDraftDirectPolicies(nextPolicies);
    setDirectPoliciesLoaded(true);
  }, [open, userSecurityPoliciesData, username]);

  const permissionManagerSelection = useMemo(() => {
    const assignedPolicySet = new Set(draftDirectPolicies.map((policy) => normalizePolicyKey(policy)));
    return simpleManagedValues.map((permission) => {
      const requiredPolicies = isPolicyBackedPermissionAccess(permission)
        ? getPoliciesForPolicyBackedPermissionAccess(permission)
        : [permission];
      return {
        permission,
        enabled: requiredPolicies.length > 0 &&
          requiredPolicies.every((policy) => assignedPolicySet.has(normalizePolicyKey(policy)))
      };
    });
  }, [draftDirectPolicies]);

  const hasUnsavedPermissionChanges =
    directPoliciesLoaded &&
    policyListFingerprint(draftDirectPolicies) !== policyListFingerprint(directPolicies);

  const stageDirectPolicies = (nextPolicies: string[]) => {
    setDraftDirectPolicies(normalizePolicyList(nextPolicies));
  };

  const handlePermissionsChange = (newPermissions: SupportPermissionState[]) => {
    if (!directPoliciesLoaded) {
      toast.error('Security policy assignments are still loading');
      return;
    }

    const enabledManagedValues = normalizePolicyList(
      (Array.isArray(newPermissions) ? newPermissions : [])
        .filter((permissionState) => permissionState?.enabled !== false)
        .map((permissionState) => `${permissionState?.permission || ''}`.trim())
        .filter(Boolean)
    );
    const enabledPolicyBackedAccesses = enabledManagedValues.filter((permission) =>
      isPolicyBackedPermissionAccess(permission)
    );
    const enabledDirectGroups = enabledManagedValues.filter((permission) =>
      !isPolicyBackedPermissionAccess(permission)
    );
    const preservedPolicies = draftDirectPolicies.filter((policy) =>
      !simpleManagedPolicyValues.includes(normalizePolicyKey(policy))
    );
    const nextSimplePolicies = applyPolicyBackedPermissionAccessPolicies(
      enabledDirectGroups,
      enabledPolicyBackedAccesses,
      simpleManagedValues.filter((permission) => isPolicyBackedPermissionAccess(permission))
    );

    stageDirectPolicies([
      ...preservedPolicies,
      ...nextSimplePolicies
    ]);
  };

  const saveDraftDirectPolicies = async () => {
    if (!username || !directPoliciesLoaded) return;
    const normalizedPolicies = normalizePolicyList(draftDirectPolicies);

    try {
      setIsSavingPermissions(true);
      const result = await updateUserSecurityPolicies({
        variables: {
          username,
          policies: normalizedPolicies.map((policy) => ({
            policy,
            allow: true,
          })),
        },
      });
      if (!result?.data?.updateUserSecurityPolicies?.success) {
        throw new Error(result?.data?.updateUserSecurityPolicies?.message || 'Failed to update permissions');
      }
      setDirectPolicies(normalizedPolicies);
      setDraftDirectPolicies(normalizedPolicies);
      await refetchUserSecurityPolicies();
      await onToolRun();
      toast.success('Permissions updated successfully');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update permissions');
    } finally {
      setIsSavingPermissions(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        contentWidth="1180px"
        contentHeight="86vh"
        contentMaxWidth="96vw"
        contentMaxHeight="88vh"
        contentTop="6vh"
        className="z-[70] flex flex-col gap-0 overflow-hidden p-0"
        overlayClassName="bg-black/30"
      >
        <DialogHeader className="shrink-0 border-b px-4 py-3 pr-12">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Shield className="h-4 w-4 text-blue-600" />
            Update Permissions
          </DialogTitle>
          <DialogDescription>
            Manage security features for {requesterName || username || 'this user'}.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-auto p-4">
          {!username && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              This ticket is not linked to a username.
            </div>
          )}

          {policiesError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              Failed to load permissions: {policiesError.message}
            </div>
          )}

          {username && policiesLoading && !directPoliciesLoaded && (
            <div className="text-sm text-gray-600">Loading permissions...</div>
          )}

          {username && directPoliciesLoaded && (
            <UserSecurityPermissionsManager
              username={username}
              tabsPosition="right"
              assignedPolicies={draftDirectPolicies}
              onChangeAssignedPolicies={stageDirectPolicies}
              headerContent={
                <p className="text-sm font-semibold text-gray-900">Permissions</p>
              }
              headerActions={
                <PermissionPresetSelect
                  selectedPermissions={permissionManagerSelection}
                  onChange={handlePermissionsChange}
                  disabled={!directPoliciesLoaded || isSavingPermissions}
                />
              }
              simplePermissions={
                <PermissionManager
                  selectedPermissions={permissionManagerSelection}
                  onChange={handlePermissionsChange}
                  disabled={!directPoliciesLoaded || isSavingPermissions}
                  hidePresetSelector
                />
              }
            />
          )}
        </div>

        <DialogFooter className="shrink-0 border-t bg-white px-4 py-3">
          <Button
            type="button"
            onClick={saveDraftDirectPolicies}
            disabled={!hasUnsavedPermissionChanges || isSavingPermissions || !directPoliciesLoaded}
          >
            {isSavingPermissions ? 'Saving...' : 'Save Permissions'}
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSavingPermissions}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const Help = () => {
  const location = useLocation();
  const { user } = useUser();
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(ACTIVE_STATUS_FILTER);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('ASC');
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [privateNote, setPrivateNote] = useState('');
  const [statusUpdate, setStatusUpdate] = useState('open');
  const [selectedAssigneeId, setSelectedAssigneeId] = useState('');
  const [activeToolDialog, setActiveToolDialog] = useState<SupportToolDialog | null>(null);
  const [toolResultUrl, setToolResultUrl] = useState('');
  const [toolResultExpiresAt, setToolResultExpiresAt] = useState('');
  const [showAccountRequests, setShowAccountRequests] = useState(false);
  const [provisionRequest, setProvisionRequest] = useState<UserAccountRequestRow | null>(null);
  const [provisionTicketId, setProvisionTicketId] = useState<number | null>(null);
  const [isProvisionDialogOpen, setIsProvisionDialogOpen] = useState(false);
  const canProvisionAccounts = hasAnyPolicy(user, ['admin:access~full']);

  const canManageSupportAssignments = useMemo(() => {
    return hasAnyPolicy(user, ['tools:support:tickets~full']);
  }, [user]);

  const filters = useMemo(() => {
    const payload: any = {
      sortBy,
      sortDirection,
      limit: 500,
      offset: 0,
    };
    const cleanedSearch = searchTerm.trim();
    if (cleanedSearch) payload.search = cleanedSearch;
    if (statusFilter !== 'all') payload.status = statusFilter;
    return payload;
  }, [searchTerm, sortBy, sortDirection, statusFilter]);

  const { data, loading, error, refetch } = useQuery(LIST_SUPPORT_TICKETS, {
    variables: { filters },
    fetchPolicy: 'network-only',
  });

  const {
    data: detailData,
    loading: detailLoading,
    error: detailError,
    refetch: refetchDetail,
  } = useQuery(GET_SUPPORT_TICKET, {
    variables: { ticketId: selectedTicketId || 0 },
    skip: !selectedTicketId,
    fetchPolicy: 'network-only',
  });

  const {
    data: linkedAccountRequestData,
  } = useQuery<{ userAccountRequestBySupportTicket?: UserAccountRequestRow | null }>(GET_ACCOUNT_REQUEST_BY_SUPPORT_TICKET, {
    variables: { ticketId: selectedTicketId || 0 },
    skip: !selectedTicketId || !canProvisionAccounts,
    fetchPolicy: 'network-only',
  });

  const { data: assignableUsersData, loading: assignableUsersLoading } = useQuery(LIST_SUPPORT_ASSIGNABLE_USERS, {
    variables: { limit: 100 },
    skip: !canManageSupportAssignments,
    fetchPolicy: 'network-only',
  });

  const [respondSupportTicket, { loading: respondLoading }] = useMutation(RESPOND_SUPPORT_TICKET);
  const [addSupportTicketPrivateNote, { loading: noteLoading }] = useMutation(ADD_SUPPORT_TICKET_PRIVATE_NOTE);
  const [updateSupportTicketStatus, { loading: statusLoading }] = useMutation(UPDATE_SUPPORT_TICKET_STATUS);
  const [assignSupportTicketUser, { loading: assigningUser }] = useMutation(ASSIGN_SUPPORT_TICKET_USER);
  const [unassignSupportTicketUser, { loading: unassigningUser }] = useMutation(UNASSIGN_SUPPORT_TICKET_USER);
  const [sendTwoFactorEnrollmentNotification, { loading: sendingTwoFactorEnrollment }] = useMutation(SEND_TOTP_ENROLLMENT_EMAIL);
  const [sendTempComplianceLogin, { loading: sendingTempLogin }] = useMutation(SEND_TEMP_COMPLIANCE_LOGIN);
  const [modifyUser, { loading: unlockingAccount }] = useMutation(MODIFY_USER);

  const tickets = useMemo(() => {
    if (!Array.isArray(data?.listSupportTickets)) return [];
    return data.listSupportTickets;
  }, [data?.listSupportTickets]);
  const visibleTickets = useMemo(() => {
    if (statusFilter !== ACTIVE_STATUS_FILTER) return tickets;

    return tickets.filter((ticket: any) => {
      const status = `${ticket?.status || ''}`.toLowerCase();
      if (status === 'open') return true;
      return status === 'pending_user';
    });
  }, [statusFilter, tickets]);
  const selectedTicket = detailData?.getSupportTicket?.ticket || null;
  const linkedAccountRequest = linkedAccountRequestData?.userAccountRequestBySupportTicket || null;
  const canCreateLinkedAccount = Boolean(
    linkedAccountRequest
      && `${linkedAccountRequest.status || ''}`.toLowerCase() === 'pending'
      && `${linkedAccountRequest.requestType || ''}`.toLowerCase() !== 'deactivation'
  );
  const loginRecoveryRequestType = `${selectedTicket?.requestType || ''}`.toLowerCase();
  const isActionableTicket = ['open', 'pending_user'].includes(`${selectedTicket?.status || ''}`.toLowerCase());
  const canRunLoginRecoveryAction = Boolean(
    canProvisionAccounts
      && isActionableTicket
      && ['password_reset', 'authenticator_reset'].includes(loginRecoveryRequestType)
  );
  const hasPrimaryTicketAction = canCreateLinkedAccount || canRunLoginRecoveryAction;
  const replies = useMemo(() => {
    if (!Array.isArray(detailData?.getSupportTicket?.replies)) return [];
    return detailData.getSupportTicket.replies;
  }, [detailData?.getSupportTicket?.replies]);
  const notes = useMemo(() => {
    if (!Array.isArray(detailData?.getSupportTicket?.notes)) return [];
    return detailData.getSupportTicket.notes;
  }, [detailData?.getSupportTicket?.notes]);
  const assignableUsers = useMemo(() => {
    if (!Array.isArray(assignableUsersData?.listSupportAssignableUsers)) return [];
    return assignableUsersData.listSupportAssignableUsers;
  }, [assignableUsersData?.listSupportAssignableUsers]);
  const assignedUsers = useMemo(() => {
    if (!Array.isArray(selectedTicket?.assignedUsers)) return [];
    return selectedTicket.assignedUsers;
  }, [selectedTicket?.assignedUsers]);
  const assignedUserIds = useMemo(() => {
    return new Set(assignedUsers.map((assigned: any) => `${assigned?.userId || ''}`).filter(Boolean));
  }, [assignedUsers]);
  const primaryAssignedUserId = assignedUsers[0]?.userId ? `${assignedUsers[0].userId}` : '';
  const assigneeSelectValue = selectedAssigneeId || primaryAssignedUserId;
  const requesterUserId = selectedTicket?.userid ? Number(selectedTicket.userid) : null;
  const requesterUsername = `${selectedTicket?.requesterUsername || selectedTicket?.username || ''}`.trim();
  const requesterName = `${selectedTicket?.requesterName || selectedTicket?.username || ''}`.trim();
  const requesterEmail = `${selectedTicket?.requesterEmail || selectedTicket?.email || ''}`.trim();
  const requesterPasswordFailures = Number(selectedTicket?.requesterPasswordFailures || 0);
  const threadMessages = useMemo(() => {
    if (!selectedTicket) return [];

    const baseMessage = {
      id: `ticket-${selectedTicket.id}`,
      message: `${selectedTicket.message || ''}`,
      messageFormat: selectedTicket.messageFormat || 'text',
      createdAt: selectedTicket.createdAt || null,
      authorName: selectedTicket.username || 'User',
      isStaff: false,
      emailSentAt: null as string | null,
      attachments: Array.isArray(selectedTicket.attachments) ? selectedTicket.attachments : [],
    };

    const replyMessages = replies.map((reply: any) => {
      const replyUserId = Number(reply?.createdByUserId || 0);
      const ticketUserId = Number(selectedTicket.userid || 0);
      const isFromUser = replyUserId > 0 && ticketUserId > 0 && replyUserId === ticketUserId;

      return {
        id: `reply-${reply.id}`,
        message: `${reply.message || ''}`,
        messageFormat: reply.messageFormat || 'text',
        createdAt: reply.createdAt || null,
        authorName: reply.username || (isFromUser ? selectedTicket.username || 'User' : 'Support'),
        isStaff: !isFromUser,
        emailSentAt: reply.emailSentAt || null,
        attachments: Array.isArray(reply.attachments) ? reply.attachments : [],
      };
    });

    const allMessages = [baseMessage, ...replyMessages];
    return allMessages.sort((left, right) => {
      const leftTime = left.createdAt ? new Date(left.createdAt).getTime() : 0;
      const rightTime = right.createdAt ? new Date(right.createdAt).getTime() : 0;
      if (leftTime !== rightTime) return leftTime - rightTime;
      return `${left.id}`.localeCompare(`${right.id}`);
    });
  }, [replies, selectedTicket]);

  useEffect(() => {
    if (!selectedTicket?.status) return;
    setStatusUpdate(selectedTicket.status);
  }, [selectedTicket?.status]);

  const runSearch = () => {
    setSearchTerm(searchInput.trim());
  };

  const resetSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  const toggleSort = (field: string) => {
    if (sortBy !== field) {
      setSortBy(field);
      setSortDirection('ASC');
      return;
    }
    if (sortDirection === 'ASC') {
      setSortDirection('DESC');
      return;
    }
    setSortDirection('ASC');
  };

  const openTicket = (ticketId: number) => {
    setSelectedTicketId(ticketId);
    setResponseMessage('');
    setPrivateNote('');
    setSelectedAssigneeId('');
  };

  const closeTicket = () => {
    setSelectedTicketId(null);
    setResponseMessage('');
    setPrivateNote('');
    setSelectedAssigneeId('');
    setActiveToolDialog(null);
    setToolResultUrl('');
    setToolResultExpiresAt('');
  };

  const openLinkedAccountRequest = () => {
    if (!selectedTicketId || !linkedAccountRequest || !canCreateLinkedAccount) return;
    setProvisionTicketId(selectedTicketId);
    setProvisionRequest(linkedAccountRequest);
    closeTicket();
    setIsProvisionDialogOpen(true);
  };

  const handleProvisionDialogOpenChange = (open: boolean) => {
    setIsProvisionDialogOpen(open);
    if (open) return;
    setProvisionRequest(null);
    setProvisionTicketId(null);
  };

  const handleLinkedAccountProvisioned = (result: UserAccountProvisionResult) => {
    const ticketId = provisionTicketId;
    if (!ticketId) return;

    void (async () => {
      try {
        await updateSupportTicketStatus({
          variables: {
            ticketId,
            status: 'resolved',
          },
        });
        await refetch();
        toast.success(`Request #${result.requestId} completed and ticket resolved.`);
      } catch (mutationError: any) {
        toast.error(mutationError?.message || 'The account was created, but the support ticket could not be resolved.');
      }
    })();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ticketId = Number(params.get('ticketId') || '');
    if (!Number.isInteger(ticketId)) return;
    if (ticketId <= 0) return;
    openTicket(ticketId);
  }, [location.search]);

  const handleRespond = async () => {
    if (!selectedTicketId) return;
    const message = responseMessage.trim();
    if (!message) {
      toast.error('Response message is required.');
      return;
    }

    try {
      await respondSupportTicket({
        variables: {
          ticketId: selectedTicketId,
          message,
          status: statusUpdate,
        },
      });
      setResponseMessage('');
      await Promise.all([refetch(), refetchDetail()]);
      toast.success('Response sent and emailed to the user.');
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Failed to send support response.');
    }
  };

  const handleAddPrivateNote = async () => {
    if (!selectedTicketId) return;
    const note = privateNote.trim();
    if (!note) {
      toast.error('Private note cannot be empty.');
      return;
    }

    try {
      await addSupportTicketPrivateNote({
        variables: {
          ticketId: selectedTicketId,
          note,
        },
      });
      setPrivateNote('');
      await Promise.all([refetch(), refetchDetail()]);
      toast.success('Private note saved.');
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Failed to save private note.');
    }
  };

  const handleUpdateStatus = async (nextStatus: string) => {
    if (!selectedTicketId) return;
    if (!nextStatus) return;
    setStatusUpdate(nextStatus);
    if (nextStatus === selectedTicket?.status) return;

    try {
      await updateSupportTicketStatus({
        variables: {
          ticketId: selectedTicketId,
          status: nextStatus,
        },
      });
      await Promise.all([refetch(), refetchDetail()]);
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Failed to update ticket status.');
      setStatusUpdate(selectedTicket?.status || 'open');
    }
  };

  const getRequesterCopyText = () => {
    if (!selectedTicket) return '';
    const lines = [
      `Support Ticket: #${selectedTicket.id}`,
      `Status: ${selectedTicket.status || 'unknown'}`,
      `Created: ${formatDateOnly(selectedTicket.createdAt)}`,
      `Updated: ${formatDateOnly(selectedTicket.updatedAt)}`,
      `User ID: ${selectedTicket.userid || 'N/A'}`,
      `Username: ${selectedTicket.requesterUsername || 'N/A'}`,
      `Name: ${selectedTicket.requesterName || selectedTicket.username || 'N/A'}`,
      `Email: ${selectedTicket.requesterEmail || selectedTicket.email || 'N/A'}`,
      `Disabled: ${selectedTicket.requesterDisabled ? 'Yes' : 'No'}`,
      `First Login: ${selectedTicket.requesterFirstLogin ? 'Yes' : 'No'}`,
      `Two-Factor Enabled: ${selectedTicket.requesterTwoFactorEnabled ?? 'N/A'}`,
      `Failed Password Attempts: ${selectedTicket.requesterPasswordFailures ?? 0}`,
      `Handler: ${selectedTicket.requesterHandlerName || selectedTicket.requesterHandlerUsername || 'N/A'}`,
      `Handler Email: ${selectedTicket.requesterHandlerEmail || 'N/A'}`,
      `Created Date: ${formatDateOnly(selectedTicket.requesterCreatedDate)}`,
      `Entry Date: ${formatDateOnly(selectedTicket.requesterEntryDate)}`,
    ];

    if (selectedTicket.requesterNote) {
      lines.push(`User Note: ${selectedTicket.requesterNote}`);
    }

    return lines.join('\n');
  };

  const handleCopyRequester = async () => {
    try {
      await copyText(getRequesterCopyText());
      toast.success('User data copied.');
    } catch (copyError: any) {
      toast.error(copyError?.message || 'Failed to copy user data.');
    }
  };

  const openSupportTool = (tool: SupportToolDialog) => {
    setToolResultUrl('');
    setToolResultExpiresAt('');
    setActiveToolDialog(tool);
  };

  const closeSupportTool = () => {
    setActiveToolDialog(null);
    setToolResultUrl('');
    setToolResultExpiresAt('');
  };

  const recordSupportToolRun = async (toolName: string) => {
    if (!selectedTicketId) return;
    const accountLabel = requesterUsername || (requesterUserId ? `user ID ${requesterUserId}` : 'unknown account');

    try {
      await addSupportTicketPrivateNote({
        variables: {
          ticketId: selectedTicketId,
          note: `Support tool ran: ${toolName} on account ${accountLabel}.`,
        },
      });
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Tool ran, but failed to add the private note.');
    }
  };

  const handleSendPasswordReset = async () => {
    if (!requesterUsername) {
      toast.error('This ticket is not linked to a username.');
      return;
    }

    try {
      const response = await sendTempComplianceLogin({
        variables: { username: requesterUsername, resetPassword: true, resetTotp: false },
      });
      const result = response?.data?.sendTempComplianceLogin;
      if (!result?.success || !result?.loginUrl) {
        throw new Error(result?.message || 'Failed to create password setup link.');
      }
      setToolResultUrl(result.loginUrl);
      setToolResultExpiresAt(result.expiresAt || '');
      await recordSupportToolRun('Send Password Reset');
      await Promise.all([refetch(), refetchDetail()]);
      toast.success(result.message || 'Password setup link sent.');
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Failed to send password setup link.');
    }
  };

  const handleSetPassword = async (password: string) => {
    if (!requesterUsername) {
      toast.error('This ticket is not linked to a username.');
      return;
    }

    try {
      await modifyUser({
        variables: {
          username: requesterUsername,
          password,
          passwordfailures: 0,
        },
      });
      await recordSupportToolRun('Set Password');
      await Promise.all([refetch(), refetchDetail()]);
      toast.success('Password updated.');
      closeSupportTool();
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Failed to set password.');
    }
  };

  const handleSendTwoFactorEnrollment = async () => {
    if (!requesterUserId) {
      toast.error('This ticket is not linked to a user ID.');
      return;
    }

    try {
      const response = await sendTwoFactorEnrollmentNotification({
        variables: { input: { userId: requesterUserId } },
      });
      const result = response?.data?.sendTwoFactorEnrollmentNotification;
      if (!result?.success) {
        throw new Error(result?.status || 'Failed to send two-factor enrollment email.');
      }
      setToolResultUrl(result.enrollmentUrl || '');
      await recordSupportToolRun('Send Two-Factor Auth');
      await Promise.all([refetch(), refetchDetail()]);
      toast.success('Two-factor enrollment email sent.');
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Failed to send two-factor enrollment email.');
    }
  };

  const handleSendOneTimeLogin = async () => {
    if (!requesterUsername) {
      toast.error('This ticket is not linked to a username.');
      return;
    }

    try {
      const response = await sendTempComplianceLogin({
        variables: { username: requesterUsername, resetPassword: true, resetTotp: true },
      });
      const result = response?.data?.sendTempComplianceLogin;
      if (!result?.success || !result?.loginUrl) {
        throw new Error(result?.message || 'Failed to send one-time login.');
      }
      setToolResultUrl(result.loginUrl);
      setToolResultExpiresAt(result.expiresAt || '');
      await recordSupportToolRun('Send One-Time Login');
      await Promise.all([refetch(), refetchDetail()]);
      toast.success(result.message || 'One-time login link sent.');
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Failed to send one-time login.');
    }
  };

  const resolveLoginRecoveryTicket = async (toolName: string) => {
    if (!selectedTicketId) return;
    await recordSupportToolRun(toolName);
    await updateSupportTicketStatus({
      variables: { ticketId: selectedTicketId, status: 'resolved' },
    });
    await Promise.all([refetch(), refetchDetail()]);
  };

  const handleLoginRecoveryAction = async () => {
    if (!requesterUsername) {
      toast.error('This ticket is not linked to a username.');
      return;
    }

    if (loginRecoveryRequestType !== 'password_reset' && loginRecoveryRequestType !== 'authenticator_reset') return;
    const isPasswordReset = loginRecoveryRequestType === 'password_reset';
    try {
      const response = await sendTempComplianceLogin({
        variables: {
          username: requesterUsername,
          resetPassword: isPasswordReset,
          resetTotp: !isPasswordReset,
        },
      });
      const result = response?.data?.sendTempComplianceLogin;
      if (!result?.success || !result?.loginUrl) {
        throw new Error(result?.message || `Failed to send ${isPasswordReset ? 'password' : 'authenticator'} reset link.`);
      }
      setToolResultUrl(result.loginUrl);
      setToolResultExpiresAt(result.expiresAt || '');
      await resolveLoginRecoveryTicket(isPasswordReset ? 'Send Password Reset' : 'Reset Authenticator');
      toast.success(result.message || `${isPasswordReset ? 'Password' : 'Authenticator'} reset link sent and ticket resolved.`);
    } catch (mutationError: any) {
      toast.error(mutationError?.message || `Failed to send ${isPasswordReset ? 'password' : 'authenticator'} reset link.`);
    }
  };

  const handleUnlockAccount = async () => {
    if (!requesterUsername) {
      toast.error('This ticket is not linked to a username.');
      return;
    }

    try {
      await modifyUser({
        variables: {
          username: requesterUsername,
          passwordfailures: 0,
        },
      });
      await recordSupportToolRun('Unlock Account');
      await Promise.all([refetch(), refetchDetail()]);
      toast.success('Account unlocked.');
      closeSupportTool();
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Failed to unlock account.');
    }
  };

  const downloadAttachment = (attachment: any) => {
    if (!attachment?.data) {
      toast.error('Attachment data is not available.');
      return;
    }

    try {
      const binaryString = atob(attachment.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let index = 0; index < binaryString.length; index += 1) {
        bytes[index] = binaryString.charCodeAt(index);
      }
      const blob = new Blob([bytes], { type: attachment.mimeType || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (downloadError: any) {
      toast.error(downloadError?.message || 'Failed to download attachment.');
    }
  };

  const handleAssignUser = async () => {
    if (!selectedTicketId) return;
    const userId = Number(selectedAssigneeId || 0);
    if (!userId) {
      toast.error('Select a user to add.');
      return;
    }

    try {
      await assignSupportTicketUser({ variables: { ticketId: selectedTicketId, userId } });
      setSelectedAssigneeId('');
      await Promise.all([refetch(), refetchDetail()]);
      toast.success('User added to the ticket.');
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Failed to add user to ticket.');
    }
  };

  const handleUnassignUser = async (userId: number) => {
    if (!selectedTicketId || !userId) return;

    try {
      await unassignSupportTicketUser({ variables: { ticketId: selectedTicketId, userId } });
      await Promise.all([refetch(), refetchDetail()]);
      toast.success('User removed from the ticket.');
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Failed to remove user from ticket.');
    }
  };

  const renderMessageBody = (message: any) => {
    const body = `${message?.message || ''}`;
    if (!body) return <span>—</span>;

    const shouldRenderHtml = `${message?.messageFormat || ''}`.toLowerCase() === 'html' || looksLikeHtml(body);
    if (shouldRenderHtml) {
      return (
        <div
          className="prose prose-sm max-w-none break-words [overflow-wrap:anywhere]"
          dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(body) }}
        />
      );
    }

    return <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{body}</div>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Support Ticket System</h1>
        {canProvisionAccounts && (
          <Button
            id="support-account-requests-toggle"
            type="button"
            variant="outline"
            aria-expanded={showAccountRequests}
            aria-controls="support-account-requests"
            onClick={() => setShowAccountRequests(current => !current)}
          >
            <UserPlus className="h-4 w-4" />
            Account requests
          </Button>
        )}
      </div>

      {canProvisionAccounts && showAccountRequests && (
        <section id="support-account-requests" aria-label="Account requests">
          {/* Reuse the Admin workflow and its existing server permissions. */}
          <ProvisionUserAccountsPage />
        </section>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageCircleReply className="h-5 w-5 text-blue-600" />
            Ticket Queue
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex gap-2 w-full lg:w-[480px]">
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search by id, user, email, or message"
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return;
                  runSearch();
                }}
              />
              <Button onClick={runSearch} className="gap-2">
                <Search className="h-4 w-4" />
                Search
              </Button>
              <Button variant="outline" onClick={resetSearch}>Clear</Button>
            </div>
            <div className="w-[220px]">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
              Failed to load support tickets: {error.message}
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button className="flex items-center gap-1" onClick={() => toggleSort('id')}>
                    ID
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center gap-1" onClick={() => toggleSort('username')}>
                    Requestor
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center gap-1" onClick={() => toggleSort('status')}>
                    Status
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center gap-1" onClick={() => toggleSort('ageHours')}>
                    Age
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center gap-1" onClick={() => toggleSort('createdAt')}>
                    Created
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </TableHead>
                <TableHead>
                  <button className="flex items-center gap-1" onClick={() => toggleSort('updatedAt')}>
                    Updated
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </TableHead>
                <TableHead>Replies</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Assigned</TableHead>
                <TableHead>Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={10} className="text-sm text-gray-500">Loading tickets…</TableCell>
                </TableRow>
              )}
              {!loading && visibleTickets.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-sm text-gray-500">No support tickets matched this filter.</TableCell>
                </TableRow>
              )}
              {!loading && visibleTickets.map((ticket: any) => (
                <TableRow
                  key={ticket.id}
                  className="cursor-pointer"
                  onClick={() => openTicket(Number(ticket.id))}
                >
                  <TableCell className="font-medium">#{ticket.id}</TableCell>
                  <TableCell>
                    <div className="font-medium text-gray-900">{ticket.username || 'Unknown User'}</div>
                    <div className="text-xs text-gray-500">{ticket.email || 'No email'}</div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={getStatusBadgeClasses(ticket.status, !!ticket.isOverdue, Number(ticket.ageHours || 0))}
                    >
                      {ticket.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={getAgeBadgeClasses(ticket.status, Number(ticket.ageHours || 0))}>
                      <Clock3 className="h-3 w-3 mr-1" />
                      {ageLabel(Number(ticket.ageHours || 0))}
                    </Badge>
                  </TableCell>
                  <TableCell className={ticket.status === 'open' && ticket.isOverdue ? 'text-red-700 font-medium' : ''}>
                    {formatDateTime(ticket.createdAt)}
                  </TableCell>
                  <TableCell
                    className={
                      ticket.status === 'pending_user' && Number(ticket.ageHours || 0) >= STALE_PENDING_USER_HOURS
                        ? 'text-slate-500'
                        : ticket.status === 'pending_user'
                          ? 'text-blue-700 font-medium'
                          : ''
                    }
                  >
                    {formatDateTime(ticket.updatedAt)}
                  </TableCell>
                  <TableCell>{ticket.responseCount || 0}</TableCell>
                  <TableCell>{ticket.noteCount || 0}</TableCell>
                  <TableCell className="max-w-[220px]">
                    {Array.isArray(ticket.assignedUsers) && ticket.assignedUsers.length > 0
                      ? ticket.assignedUsers.map((item: any) => item.name || item.username).join(', ')
                      : '—'}
                  </TableCell>
                  <TableCell className="max-w-[360px]">{truncate(ticket.message, 110)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={selectedTicketId !== null} onOpenChange={(open: boolean) => { if (!open) closeTicket(); }}>
        <DialogContent
          contentWidth="99vw"
          contentMaxWidth="99vw"
          contentHeight="96vh"
          contentMaxHeight="96vh"
          contentTop="2vh"
          className="flex flex-col gap-0 overflow-hidden p-0"
        >
          <DialogHeader className="shrink-0 border-b px-4 py-2 pr-12">
            <DialogTitle className="flex items-center gap-4 text-xs font-normal text-gray-600">
              {selectedTicket && (() => {
                const currentStatus = statusUpdate || selectedTicket.status;
                return (
                  <>
                    <Badge variant="outline" className={getAgeBadgeClasses(currentStatus, Number(selectedTicket.ageHours || 0))}>
                      <Clock3 className="mr-1 h-3 w-3" />
                      {ageLabel(Number(selectedTicket.ageHours || 0))}
                    </Badge>
                    <span>Ticket #{selectedTicketId}</span>
                    <span className="text-xs font-normal text-gray-600">Created: {formatDateOnly(selectedTicket.createdAt)}</span>
                    <span className="text-xs font-normal text-gray-600">Updated: {formatDateOnly(selectedTicket.updatedAt)}</span>
                    <span className="text-xs font-normal text-gray-600">Last response: {formatDateOnly(selectedTicket.lastResponseAt)}</span>
                    <span className="text-xs font-normal text-gray-600">Closed: {formatDateOnly(selectedTicket.closedAt)}</span>
                    <div className="ml-auto flex items-center gap-2">
                      <Label htmlFor="ticket-status-update" className="text-xs font-normal text-gray-600">Status</Label>
                      <Select value={statusUpdate} onValueChange={handleUpdateStatus}>
                        <SelectTrigger id="ticket-status-update" className="h-8 w-[170px] bg-white text-sm font-normal">
                          <SelectValue placeholder="Ticket status" />
                        </SelectTrigger>
                        <SelectContent>
                          {writableStatusOptions.map((item) => (
                            <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {statusLoading && <span className="text-xs font-normal text-gray-500">Updating...</span>}
                    </div>
                  </>
                );
              })()}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Respond to the user, manage status, assignments, and private notes.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-hidden p-4">
            {detailError && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
                Failed to load ticket detail: {detailError.message}
              </div>
            )}

            {detailLoading && (
              <div className="text-sm text-gray-600">Loading ticket detail…</div>
            )}

            {!detailLoading && selectedTicket && (
              <div className="flex h-full min-h-0 flex-col gap-3">
                <div className="rounded-md border bg-white px-3 py-2">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                    <div className="min-w-0 max-w-[280px] truncate">
                      <span className="font-medium text-gray-900">Name:</span> {selectedTicket.requesterName || selectedTicket.username || 'Unknown User'}
                    </div>
                    <div><span className="font-medium text-gray-900">User ID:</span> {selectedTicket.userid || 'N/A'}</div>
                    <div className="min-w-0 max-w-[320px] truncate"><span className="font-medium text-gray-900">Email:</span> {selectedTicket.requesterEmail || selectedTicket.email || 'N/A'}</div>
                    <div><span className="font-medium text-gray-900">2FA:</span> {selectedTicket.requesterTwoFactorEnabled ?? 'N/A'}</div>
                    <div><span className="font-medium text-gray-900">Failed:</span> {selectedTicket.requesterPasswordFailures ?? 0}</div>
                    <div><span className="font-medium text-gray-900">Disabled:</span> {selectedTicket.requesterDisabled ? 'Yes' : 'No'}</div>
                    <div className="min-w-0 max-w-[280px] truncate"><span className="font-medium text-gray-900">Handler:</span> {selectedTicket.requesterHandlerName || selectedTicket.requesterHandlerUsername || 'N/A'}</div>
                    <div><span className="font-medium text-gray-900">Created:</span> {formatDateOnly(selectedTicket.requesterCreatedDate)}</div>
                    <div><span className="font-medium text-gray-900">Entry:</span> {formatDateOnly(selectedTicket.requesterEntryDate)}</div>
                    <div><span className="font-medium text-gray-900">Last responder:</span> {selectedTicket.lastResponderName || 'None'}</div>
                    {canCreateLinkedAccount && (
                      <Button
                        id="support-account-request-create"
                        type="button"
                        size="sm"
                        onClick={openLinkedAccountRequest}
                        className="ml-auto h-7 gap-1 bg-[var(--semantic-success)] px-2 text-xs text-[var(--surface-panel-solid)] hover:bg-[var(--semantic-success)]"
                      >
                        <UserPlus className="h-3.5 w-3.5" />
                        New User Request
                      </Button>
                    )}
                    {canRunLoginRecoveryAction && (
                      <Button
                        id="support-login-recovery-action"
                        type="button"
                        size="sm"
                        onClick={() => openSupportTool(
                          loginRecoveryRequestType === 'password_reset'
                            ? 'ticket-password-reset'
                            : 'ticket-authenticator-reset'
                        )}
                        disabled={sendingTempLogin}
                        className="ml-auto h-7 gap-1 bg-[var(--semantic-success)] px-2 text-xs text-[var(--surface-panel-solid)] hover:bg-[var(--semantic-success)]"
                      >
                        {loginRecoveryRequestType === 'password_reset' ? (
                          <KeyRound className="h-3.5 w-3.5" />
                        ) : (
                          <Smartphone className="h-3.5 w-3.5" />
                        )}
                        {loginRecoveryRequestType === 'password_reset' ? 'Send password reset' : 'Reset authenticator'}
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={handleCopyRequester} className={`${hasPrimaryTicketAction ? '' : 'ml-auto'} h-7 gap-1 px-2 text-xs`}>
                      <Copy className="h-3.5 w-3.5" />
                      Copy
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs">
                          <Wrench className="h-3.5 w-3.5" />
                          Tools
                          <ChevronDown className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        {SUPPORT_TOOL_MENU_ITEMS.map(({ tool, label, requirement, Icon }) => (
                          <DropdownMenuItem
                            key={tool}
                            onSelect={() => openSupportTool(tool)}
                            disabled={requirement === 'userId' ? !requesterUserId : !requesterUsername}
                          >
                            <Icon className="mr-2 h-4 w-4" />
                            {label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_370px]">
                  <section className="flex min-h-0 flex-col rounded-md border bg-white">
                    <div className="flex shrink-0 items-center justify-between border-b px-3 py-2">
                      <div className="text-sm font-semibold text-gray-900">Conversation</div>
                      <div className="text-xs text-gray-500">{threadMessages.length} messages</div>
                    </div>
                    <div className="min-h-0 flex-1 overflow-auto space-y-3 p-3">
                      {threadMessages.length === 0 && (
                        <div className="text-sm text-gray-500">No messages yet.</div>
                      )}
                      {threadMessages.map((message: any) => (
                        <div
                          key={message.id}
                          className={`flex ${message.isStaff ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className="flex max-w-[90%] flex-col items-start gap-1 text-left">
                            <div className="text-xs text-gray-500">
                              {message.authorName || 'User'} · {formatDateTime(message.createdAt)}
                              {message.isStaff && message.emailSentAt ? ` · emailed ${formatDateTime(message.emailSentAt)}` : ''}
                            </div>
                            <div
                              className={`w-full rounded-md border px-3 py-2 text-left text-sm ${
                                message.isStaff
                                  ? 'bg-blue-50 text-blue-950 border-blue-300 border-r-4'
                                  : 'bg-amber-50 text-amber-950 border-amber-300 border-l-4'
                              }`}
                            >
                              {renderMessageBody(message)}
                              {Array.isArray(message.attachments) && message.attachments.length > 0 && (
                                <div className="mt-3 space-y-1 border-t pt-2">
                                  <div className="flex items-center gap-1 text-xs font-medium text-gray-600">
                                    <Paperclip className="h-3.5 w-3.5" />
                                    Attachments
                                  </div>
                                  {message.attachments.map((attachment: any) => (
                                    <button
                                      key={attachment.id || attachment.filename}
                                      type="button"
                                      onClick={() => downloadAttachment(attachment)}
                                      className="flex w-full items-center justify-between gap-2 rounded border bg-white px-2 py-1 text-left text-xs text-gray-700 hover:bg-gray-50"
                                    >
                                      <span className="min-w-0 truncate">{attachment.filename || 'attachment'}</span>
                                      <span className="flex shrink-0 items-center gap-1 text-gray-500">
                                        {formatBytes(attachment.sizeBytes)}
                                        <Download className="h-3.5 w-3.5" />
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="shrink-0 space-y-2 border-t p-3">
                      <Label htmlFor="support-response">Reply</Label>
                      <div className="relative">
                        <Textarea
                          id="support-response"
                          value={responseMessage}
                          onChange={(event) => setResponseMessage(event.target.value)}
                          rows={4}
                          placeholder="Type message and send to the user..."
                          disabled={respondLoading}
                          className="resize-none pr-12"
                        />
                        <Button
                          type="button"
                          onClick={handleRespond}
                          disabled={respondLoading || !responseMessage.trim()}
                          size="sm"
                          aria-label="Send reply"
                          title="Send reply"
                          className="absolute right-2 top-2 h-8 w-8 p-0"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </section>

                  <aside className="flex min-h-0 flex-col gap-3 overflow-hidden">
                    <section className="shrink-0 rounded-md border bg-white">
                      <div className="flex items-center gap-2 border-b px-3 py-2 text-sm font-semibold text-gray-900">
                        <Users className="h-4 w-4" />
                        Assigned Support
                      </div>
                      <div className="space-y-3 p-3">
                        {canManageSupportAssignments && (
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <Select value={assigneeSelectValue} onValueChange={setSelectedAssigneeId}>
                                <SelectTrigger className="h-8 min-w-0 flex-1">
                                  <SelectValue placeholder={assignableUsersLoading ? 'Loading support users...' : 'Unassigned'} />
                                </SelectTrigger>
                                <SelectContent>
                                  {assignableUsers.map((assignable: any) => (
                                    <SelectItem key={assignable.userId} value={`${assignable.userId}`}>
                                      {assignable.name || assignable.username} {assignable.email ? `(${assignable.email})` : ''}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                onClick={handleAssignUser}
                                disabled={assigningUser || !selectedAssigneeId || assignedUserIds.has(selectedAssigneeId)}
                                size="sm"
                                className="h-8 gap-1"
                              >
                                <UserPlus className="h-4 w-4" />
                                Assign
                              </Button>
                            </div>
                            {assignedUsers.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {assignedUsers.map((assigned: any) => (
                                  <Badge key={assigned.userId} variant="outline" className="gap-1 py-1">
                                    {assigned.name || assigned.email}
                                    {canManageSupportAssignments && (
                                      <button
                                        type="button"
                                        onClick={() => handleUnassignUser(Number(assigned.userId))}
                                        disabled={unassigningUser}
                                        className="ml-1 rounded hover:bg-gray-100"
                                        aria-label={`Remove ${assigned.name || assigned.email}`}
                                      >
                                        <X className="h-3 w-3" />
                                      </button>
                                    )}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </section>

                    <section className="flex min-h-0 flex-1 flex-col rounded-md border bg-white">
                      <div className="shrink-0 border-b px-3 py-2 text-sm font-semibold text-gray-900">Private Notes</div>
                      <div className="min-h-0 flex-1 overflow-auto space-y-2 p-3">
                        {notes.length === 0 && (
                          <div className="text-sm text-gray-500">No private notes yet.</div>
                        )}
                        {notes.map((note: any) => (
                          <div key={note.id} className="rounded-md border bg-amber-50/40 p-3">
                            <div className="mb-1 text-xs text-gray-600">
                              {note.username || 'Support'} · {formatDateTime(note.createdAt)}
                            </div>
                            <div className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{note.note}</div>
                          </div>
                        ))}
                      </div>
                      <div className="shrink-0 space-y-2 border-t p-3">
                        <Label htmlFor="support-private-note">Add Private Note</Label>
                        <div className="relative">
                          <Textarea
                            id="support-private-note"
                            value={privateNote}
                            onChange={(event) => setPrivateNote(event.target.value)}
                            rows={4}
                            placeholder="Internal note (not emailed to user)"
                            disabled={noteLoading}
                            className="resize-none pr-12"
                          />
                          <Button
                            type="button"
                            onClick={handleAddPrivateNote}
                            disabled={noteLoading || !privateNote.trim()}
                            size="sm"
                            aria-label="Add private note"
                            title="Add private note"
                            className="absolute right-2 top-2 h-8 w-8 p-0"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </section>
                  </aside>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ProvisionUserAccountRequestDialog
        request={provisionRequest}
        open={isProvisionDialogOpen}
        onOpenChange={handleProvisionDialogOpenChange}
        onProvisioned={handleLinkedAccountProvisioned}
      />

      <SupportToolActionDialog
        open={activeToolDialog === 'reset-password'}
        title="Send Password Reset"
        description="Send the same password setup link used by User Manager."
        actionLabel="Send Reset Link"
        loading={sendingTempLogin}
        disabled={!requesterUsername}
        icon={<Send className="h-4 w-4 text-blue-600" />}
        requesterName={requesterName}
        username={requesterUsername}
        email={requesterEmail}
        userId={requesterUserId}
        resultUrl={toolResultUrl}
        resultUrlLabel="Password Setup Link"
        resultExpiresAt={toolResultExpiresAt}
        onConfirm={handleSendPasswordReset}
        onOpenChange={(open) => { if (!open) closeSupportTool(); }}
      />

      <SupportToolActionDialog
        open={activeToolDialog === 'ticket-password-reset'}
        title="Send Password Reset"
        description="Send a password setup link to this user and resolve the support ticket."
        actionLabel="Send Reset Link"
        loading={sendingTempLogin}
        disabled={!requesterUsername}
        icon={<KeyRound className="h-4 w-4 text-blue-600" />}
        requesterName={requesterName}
        username={requesterUsername}
        email={requesterEmail}
        userId={requesterUserId}
        resultUrl={toolResultUrl}
        resultUrlLabel="Password Setup Link"
        resultExpiresAt={toolResultExpiresAt}
        onConfirm={handleLoginRecoveryAction}
        onOpenChange={(open) => { if (!open) closeSupportTool(); }}
      />

      <SupportToolActionDialog
        open={activeToolDialog === 'ticket-authenticator-reset'}
        title="Reset Authenticator"
        description="Send an authenticator reset link to this user and resolve the support ticket."
        actionLabel="Send Authenticator Reset"
        loading={sendingTempLogin}
        disabled={!requesterUsername}
        icon={<Smartphone className="h-4 w-4 text-blue-600" />}
        requesterName={requesterName}
        username={requesterUsername}
        email={requesterEmail}
        userId={requesterUserId}
        resultUrl={toolResultUrl}
        resultUrlLabel="Authenticator Reset Link"
        resultExpiresAt={toolResultExpiresAt}
        onConfirm={handleLoginRecoveryAction}
        onOpenChange={(open) => { if (!open) closeSupportTool(); }}
      />

      <SupportSetPasswordDialog
        open={activeToolDialog === 'set-password'}
        loading={unlockingAccount}
        requesterName={requesterName}
        username={requesterUsername}
        email={requesterEmail}
        userId={requesterUserId}
        onSetPassword={handleSetPassword}
        onOpenChange={(open) => { if (!open) closeSupportTool(); }}
      />

      <SupportPermissionsDialog
        open={activeToolDialog === 'permissions'}
        username={requesterUsername}
        requesterName={requesterName}
        onToolRun={async () => {
          await recordSupportToolRun('Update Permissions');
          await Promise.all([refetch(), refetchDetail()]);
        }}
        onOpenChange={(open) => { if (!open) closeSupportTool(); }}
      />

      <SupportToolActionDialog
        open={activeToolDialog === 'two-factor'}
        title="Send Two-Factor Auth"
        description="Send the same two-factor enrollment notification used by User Manager."
        actionLabel="Send Enrollment Email"
        loading={sendingTwoFactorEnrollment}
        disabled={!requesterUserId}
        icon={<Smartphone className="h-4 w-4 text-blue-600" />}
        requesterName={requesterName}
        username={requesterUsername}
        email={requesterEmail}
        userId={requesterUserId}
        resultUrl={toolResultUrl}
        resultUrlLabel="Enrollment Page"
        onConfirm={handleSendTwoFactorEnrollment}
        onOpenChange={(open) => { if (!open) closeSupportTool(); }}
      />

      <SupportToolActionDialog
        open={activeToolDialog === 'one-time-login'}
        title="Send One-Time Login"
        description="Send the temporary support login link for this user."
        actionLabel="Send One-Time Login"
        loading={sendingTempLogin}
        disabled={!requesterUsername}
        icon={<Link2 className="h-4 w-4 text-blue-600" />}
        requesterName={requesterName}
        username={requesterUsername}
        email={requesterEmail}
        userId={requesterUserId}
        resultUrl={toolResultUrl}
        resultUrlLabel="One-Time Login Link"
        resultExpiresAt={toolResultExpiresAt}
        onConfirm={handleSendOneTimeLogin}
        onOpenChange={(open) => { if (!open) closeSupportTool(); }}
      />

      <SupportToolActionDialog
        open={activeToolDialog === 'unlock'}
        title="Unlock Account"
        description="Reset failed password attempts for this account."
        actionLabel="Unlock Account"
        loading={unlockingAccount}
        disabled={!requesterUsername}
        icon={<LockOpen className="h-4 w-4 text-blue-600" />}
        requesterName={requesterName}
        username={requesterUsername}
        email={requesterEmail}
        userId={requesterUserId}
        failedAttempts={requesterPasswordFailures}
        onConfirm={handleUnlockAccount}
        onOpenChange={(open) => { if (!open) closeSupportTool(); }}
      />
    </div>
  );
};
